import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { DefaultRate } from '../../../../models/DefaultRate'
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import NotificationService from '../../../../services/NotificationService'
import { ChannelFacility } from '../../../../models/ChannelFacility'
import Venue from '../../../../models/Venue'
import RatesApi from '../../../../services/api-clients/RatesApi'
import Event from '../../../../models/Event'
import type { GetEventsByLocationResponseItem } from '../../../../models/Event'
import { Temporal } from '@js-temporal/polyfill'
import { Inventory } from '../../../../models/Inventory'

/**
 * This composable encapsulates data needed to build items for the Rate Management calendar.
 */
export function useLocationData(
  location: Ref<null | Location>,
  start: Ref<null | Temporal.PlainDate>,
  end: Ref<null | Temporal.PlainDate>,
) {

  const { t } = useI18n()
  const verticalsToShow = VerticalRegistry.getMany(['showOnRateManagementCalendar']).map((vertical) => vertical.name)

  const loadingLocationProducts = ref<boolean>(false)
  const loadingRates = ref<boolean>(false)
  const channelFacilitiesAlreadyLoadedForLocationIds: number[] = []
  const venuesAlreadyLoadedForLocationIds: number[] = []
  const rawEvents = ref<GetEventsByLocationResponseItem[]>([])

  /**
   * All location data
   */

  watch(location, () => {

    if (!location.value) {
      return
    }

    const locationId = location.value.ID

    Promise.all([
      loadLocationProducts(locationId),
      loadChannelFacilities(locationId),
      loadVenues(locationId),
    ]).then(() => {
      loadRates(locationId)
    })

  }, {immediate: true})

  /**
   * Location Products
   */

  function loadLocationProducts(locationId: number): Promise<any> {

    loadingLocationProducts.value = true

    return LocationProduct.fetchByLocationID(locationId)
      .catch(e => {
        NotificationService.error(t('alerts.couldnt_load_products'))
        throw e
      })
      .finally(() => {
        loadingLocationProducts.value = false
      })

  }

  const locationProducts = computed<LocationProduct[]>(() => {

    if (location.value === null) {
      return []
    }

    return LocationProduct.query()
      .with(['product'])
      .where('locationID', location.value.ID)
      .whereHas('product', (query) => {
        query.where('verticalType', verticalsToShow)
      })
      .with('venueLocationProducts', (query) => {
        query.where('live', true)
      })
      .with('venueLocationProducts.venue')
      .with('venueLocationProducts.venueTiers', (query) => {
        query.orderBy('tierInventoryPercentage')
      })
      .get()
      .sort((lp1, lp2) => {

        const name1 = lp1.product?.name
        const name2 = lp2.product?.name

        if (name1 !== undefined && name2 === undefined) {
          return -1 // lp1 comes first
        }

        if (name2 === undefined && name2 !== undefined) {
          return 1 // lp2 comes first
        }

        if (name1 === undefined && name2 === undefined) {
          return 0 // literally don't care
        }

        return name1.localeCompare(name2)

      })
      .sort((lp1, lp2) => {
        const weight1 = VerticalRegistry.get(lp1.product?.verticalType)?.sortWeight ?? 1000
        const weight2 = VerticalRegistry.get(lp2.product?.verticalType)?.sortWeight ?? 1000
        return weight1 - weight2
      })

  })

  /**
   * Load ChannelFacilities for the currently selected location. Returns a
   * promise.
   */
  function loadChannelFacilities(locationId: number): Promise<any> {

    if (channelFacilitiesAlreadyLoadedForLocationIds.includes(locationId)) {
      return Promise.resolve()
    }

    return ChannelFacility.fetchByLocationID(locationId)
      .then(() => {
        channelFacilitiesAlreadyLoadedForLocationIds.push(locationId)
      })
      .catch(e => {
        console.error(e)
        NotificationService.error('Couldn\'t load channel facilities!') // TODO: i18n
        throw e
      })

  }

  /**
   * Load Venues (with associated VLPs and venueTiers) for the currently
   * selected location into the store. Returns a promise.
   */
  function loadVenues(locationId: number): Promise<any> {

    if (venuesAlreadyLoadedForLocationIds.includes(locationId)) {
      return Promise.resolve()
    }

    return Venue.fetchLocationVenues(locationId)
      .then(() => {
        venuesAlreadyLoadedForLocationIds.push(locationId)
      })
      .catch(e => {
        console.error(e)
        NotificationService.error('Couldn\'t load venues!') // TODO: i18n
        throw e
      })

  }

  /**
   * Rates
   */

  watch([start, end], () => {
    if (!location.value) {
      return
    }
    loadRates(location.value.ID)
  })

  let loadRatesAbortController: null | AbortController = null

  function loadRates(locationId: number): void {

    if (loadRatesAbortController) {
      loadRatesAbortController.abort()
      loadRatesAbortController = null
    }

    if (!location.value || !start.value || !end.value) {
      return
    }

    loadingRates.value = true
    loadRatesAbortController = new AbortController()

    const ratesPromise = RatesApi
      .fetchByLocationID(locationId, {
        start: start.value.toString(),
        end: end.value.toString(),
        include_event_tiers: true,
      }, loadRatesAbortController.signal).catch((error) => {
        if (axios.isCancel(error)) {
          return
        }
        console.error(error)
        NotificationService.error('Couldn\'t load rates!')
      })

    const eventsPromise = Event.getByLocationId(locationId, {
      start: start.value,
      end: end.value,
      signal: loadRatesAbortController.signal,
    }).then(result => {
      rawEvents.value = result
    }).catch((error) => {
      if (axios.isCancel(error)) {
        return
      }
      console.error(error)
      NotificationService.error('Couldn\'t load events!')
    })

    Promise.all([ratesPromise, eventsPromise]).finally(() => {
      loadingRates.value = false
    })

  }

  /**
   * DefaultRates for the selected location, along with SpotRates which overlap
   * with the date range covered by the calendar, and some additional relations
   * that we need.
   */
  const defaultRatesByLocationProductId = computed<{[key: number]: DefaultRate[]}>(() => {

    const output: {[key: number]: DefaultRate[]} = {}
    const validCdrStatuses = ['created', 'pending update', 'updated', 'failed update']

    locationProducts.value.forEach(lp => {

      const query = DefaultRate.query()
        .where('locationProductID', lp.ID)
        .orderBy((defaultRate) => (defaultRate as DefaultRate).getDurationInHours())
        .with('channelDefaultRates', (query) => {
          query.where('status', (status: string) => validCdrStatuses.includes(status)).with('channelFacility').orderBy('channel')
        })
        .with('venueLocationProducts.venue')
        .with('locationProduct.product')

      if (start.value && end.value) {

        const isoStart = start.value.toString()
        const isoEnd = end.value.toString()

        query.with('spotRates', (query) => {
          query
            .with('channelFacility')
            .where('startDate', (startDate: string) => {
              return startDate <= isoEnd
            })
            .where('endDate', (endDate: string) => {
              return endDate >= isoStart
            })
        })
      }

      output[lp.ID] = query.get().filter((dr) => {
        return (
          // only include DRs that have at least one CDR past "pending create" status, or are for the Event vertical
          dr.locationProduct.product.verticalType === 'event' ||
          dr.channelDefaultRates.some((cdr) => validCdrStatuses.includes(cdr.status))
        )
      })

    })

    return output

  })

  const inventoriesWithEventTiers = computed<Inventory[]>(() => {

    if (!start.value || !end.value) {
      return []
    }

    const isoStart = start.value.toString()
    const isoEnd = end.value.toString()

    const locationProductIds = locationProducts.value.map(lp => lp.ID)

    return Inventory.query()
      .where('locationProductID', (locationProductID: number) => {
        return locationProductIds.includes(locationProductID)
      })
      .where('date', (date: string) => date >= isoStart && date <= isoEnd)
      .has('eventTiers')
      .with(['eventInventoryTier', 'eventTiers', 'event', 'event.venue'])
      .get()

  })

  return {
    locationProducts,
    loadingLocationProducts,
    loadRates,
    loadingRates,
    defaultRatesByLocationProductId,
    inventoriesWithEventTiers,
    rawEvents,
  }

}
